import type { FC } from 'react';
import React from 'react';

import { type Placement } from '@atlaskit/popper';

import { type DataClassificationLevelProps } from '@atlassian/data-classification-level';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { LoadableLazy } from '@confluence/loadable';

import type { ContentClassificationMetadata } from '../hooks/useContentClassification.ts';
import {
	GenericClassificationTagButton,
	TagButtonMenuBehavior,
} from '../shared/GenericClassificationTagButton.tsx';
import { TagLoadingAndFlagWrapper } from '../shared/TagLoadingAndFlagWrapper.tsx';
import { useEditSubmitCallback } from '../hooks/useEditSubmitCallback.ts';
import { TagButtonUIVariant } from '../constants/TagButtonUIVariant.ts';
import { FlagStatus } from '../constants/FlagStatus.ts';
import { ErrorState } from '../constants/ErrorState.ts';

const ClassificationTagButtonError = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ClassificationTagButtonError" */ '../shared/ClassificationTagButtonError.tsx'
			)
		).ClassificationTagButtonError,
});

export type ContentTypeClassificationProps = {
	spaceKey: string;
	contentId: string;
	contentType: string;
	contentClassificationMetadata: ContentClassificationMetadata;
	userOffline?: boolean;
	readOnly?: boolean;
	variant?: TagButtonUIVariant;
	onEditMenuToggle?: () => void;
	onReadMenuToggle?: () => void;
	popupPlacementOverride?: Placement;
	spacing?: DataClassificationLevelProps['spacing'];
	onRestoreFocus?: () => void;
	buttonTestId?: string;
};

const ContentTypeClassificationComponent = ({
	spaceKey,
	contentId,
	contentType,
	contentClassificationMetadata,
	readOnly = true,
	variant = TagButtonUIVariant.default,
	popupPlacementOverride,
	spacing,
	onRestoreFocus,
	buttonTestId,
}: ContentTypeClassificationProps) => {
	const handleSubmit = useEditSubmitCallback({
		contentId,
		contentClassificationMetadata,
	});

	const {
		classification: currentClassification,
		hasNoClassification,
		shouldDisplayClassification,
		flagStatus,
		errorState,
		contentLevelError,
		spaceDefaultIsOverridden,
		source,
		canManageSpaceSettings,
		classificationControlSetting,
		userOverrideSetting,
	} = contentClassificationMetadata;

	const isLoading = flagStatus === FlagStatus.LOADING;

	const isAddNewClassificationMode =
		!readOnly && hasNoClassification && !shouldDisplayClassification;

	if (isAddNewClassificationMode) {
		return null;
	}

	if (!currentClassification || errorState === ErrorState.NULL) return null;

	if (errorState === ErrorState.MESSAGE) {
		return <ClassificationTagButtonError contentLevelError={contentLevelError} />;
	}

	return (
		<TagLoadingAndFlagWrapper isLoading={isLoading} flagStatus={flagStatus}>
			<GenericClassificationTagButton
				spaceKey={spaceKey}
				contentId={contentId}
				contentType={contentType}
				currentClassification={currentClassification}
				spaceDefaultIsOverridden={spaceDefaultIsOverridden}
				onEditSubmit={handleSubmit}
				menuBehavior={readOnly ? TagButtonMenuBehavior.readOnly : TagButtonMenuBehavior.readAndEdit}
				loading={isLoading}
				variant={variant}
				popupPlacementOverride={popupPlacementOverride}
				spacing={spacing}
				onRestoreFocus={onRestoreFocus}
				buttonTestId={buttonTestId}
				source={source}
				canManageSpaceSettings={canManageSpaceSettings}
				classificationControlSetting={classificationControlSetting}
				userOverrideSetting={userOverrideSetting}
			/>
		</TagLoadingAndFlagWrapper>
	);
};

const ContentTypeClassificationGetter: FC<ContentTypeClassificationProps> = (props) => {
	return <ContentTypeClassificationComponent {...props} />;
};

const ContentTypeClassificationWithErrorBoundary = withTransparentErrorBoundary({
	attribution: Attribution.DLP,
})(ContentTypeClassificationGetter);

export const ContentTypeClassification: FC<ContentTypeClassificationProps> = (props) => (
	<ContentTypeClassificationWithErrorBoundary {...props} />
);
